import React, { useState, useEffect } from 'react';
import {
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  Grid
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { shades } from '../../../theme';

// Services Offered Component
const ServicesOffered = ({ onValidityChange, onDataChange, initialData = {} }) => {
  const [formData, setFormData] = useState([
    { serviceName: '', description: '' }
  ]);

  const [errors, setErrors] = useState([{}]);
  console.log('formData type:', typeof formData, 'Value:', formData);

  // Load saved data from localStorage when component mounts
  useEffect(() => {
    const storedData = localStorage.getItem('servicesData');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (Array.isArray(parsedData)) {
          setFormData(parsedData);
          setErrors(Array(parsedData.length).fill({}));
        }
      } catch (error) {
        console.error('Error parsing stored data:', error);
      }
    }
  }, []);

  // Save data to localStorage whenever any field changes
  useEffect(() => {
    localStorage.setItem('servicesData', JSON.stringify(formData));
  }, [formData]);

  // Validate form
  const validateForm = () => {
    let isValid = true;
    const newErrors = formData.map(service => {
      const serviceErrors = {};

      if (!service.serviceName) {
        serviceErrors.serviceName = 'Service Name is required.';
        isValid = false;
      }

      return serviceErrors;
    });

    setErrors(newErrors);
    return isValid;
  };

  // Automatically check form validity whenever any field changes
  useEffect(() => {
    const isValid = formData.every(service => service.serviceName);
    onValidityChange(isValid);
    if (isValid) {
      onDataChange(formData);
    }
    validateForm();
  }, [formData, onValidityChange]);

  // Style configuration for text fields
  const textFieldStyle = {
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#9e9e9e'
      },
      '&:hover fieldset': {
        borderColor: '#1976d2'
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff !important',
        '& fieldset': {
          borderColor: '#1976d2'
        }
      }
    },
    '& .MuiInputLabel-root': {
      color: '#9e9e9e'
    },
    '& .MuiOutlinedInput-input': {
      color: shades.neutral[700]
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
      transition: 'background-color 5000s ease-in-out 0s'
    }
  };

  const addService = () => {
    setFormData([...formData, {
      serviceName: '',
      description: ''
    }]);
    setErrors([...errors, {}]);
  };

  const removeService = (index) => {
    if (formData.length <= 1) {
      alert('You must list at least one service.');
      return;
    }

    const newFormData = [...formData];
    newFormData.splice(index, 1);
    setFormData(newFormData);

    const newErrors = [...errors];
    newErrors.splice(index, 1);
    setErrors(newErrors);
  };

  const handleChange = (index, field, value) => {
    const newFormData = [...formData];
    newFormData[index][field] = value;
    setFormData(newFormData);

    // Clear error for this field if it has a value now
    if (value) {
      const newErrors = [...errors];
      if (newErrors[index] && newErrors[index][field]) {
        newErrors[index] = { ...newErrors[index], [field]: '' };
        setErrors(newErrors);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, color: 'black', height: 'auto' }}>
      <Typography variant='h6' sx={{ color: shades.neutral[700], marginBottom: 1 }}>
        Services Offered
      </Typography>

      {formData.map((service, index) => (
        <Box
          key={index}
          sx={{
            border: `1px solid ${shades.neutral[300]}`,
            padding: 3,
            borderRadius: 1,
            position: 'relative',
            mb: 3
          }}
        >
          {/* Remove button - only show if there's more than one entry */}
          {formData.length > 1 && (
            <IconButton
              onClick={() => removeService(index)}
              sx={{ position: 'absolute', top: 10, right: 10 }}
              color='error'
              size='small'
            >
              <DeleteIcon />
            </IconButton>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label='Service Name *'
                fullWidth
                value={service.serviceName}
                onChange={(e) => handleChange(index, 'serviceName', e.target.value)}
                error={!!errors[index]?.serviceName}
                helperText={errors[index]?.serviceName}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label='Description (optional)'
                multiline
                rows={4}
                fullWidth
                value={service.description}
                onChange={(e) => handleChange(index, 'description', e.target.value)}
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>
        </Box>
      ))}

      <Button
        variant='outlined'
        startIcon={<AddIcon />}
        onClick={addService}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add Another Service
      </Button>
    </Box>
  );
};

export default ServicesOffered;
